'use client';

import { c as _c } from "react/compiler-runtime";
import type { MouseEvent } from 'react';
import { useCallback, useRef, useState } from 'react';
import { pieChartAccentStyle, POWER_BAR_TRACK_CLASS, STANDARD_POWER_BAR_LAYOUT, powerBarLayoutBottomSpacerPx } from '@/domains/pages/shared/power-bar-shared';
import { ChartTooltipPanel, FloatingTooltip } from '@/domains/pages/shared/chart-tooltip';
import type { MethodologyDimension } from '@/domains/pages/ai-proficiency/fixtures/methodology';
import { methodologyTotal } from '@/domains/pages/ai-proficiency/fixtures/methodology';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
type MethodologyBreakdownBarProps = {
  dimensions: MethodologyDimension[];
  hoveredKey?: string | null;
  onHoverChange?: (key: string | null) => void;
  className?: string;
};
export function MethodologyBreakdownBar(t0) {
  const $ = _c(47);
  const {
    dimensions,
    hoveredKey: t1,
    onHoverChange,
    className
  } = t0;
  const hoveredKey = t1 === undefined ? null : t1;
  let t2;
  if ($[0] !== dimensions) {
    t2 = methodologyTotal(dimensions);
    $[0] = dimensions;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const totalPoints = t2;
  const fillPercent = Math.min(100, Math.max(0, totalPoints));
  const trackRef = useRef(null);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      x: 0,
      y: 0
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [tooltipPos, setTooltipPos] = useState(t3);
  let T0;
  let t10;
  let t11;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== className || $[4] !== dimensions || $[5] !== fillPercent || $[6] !== hoveredKey || $[7] !== onHoverChange || $[8] !== tooltipPos.x || $[9] !== tooltipPos.y) {
    const hoveredDimension = dimensions.find(dimension => dimension.key === hoveredKey) ?? null;
    let t12;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = (clientX, clientY) => {
        setTooltipPos({
          x: clientX,
          y: clientY
        });
      };
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    const updateTooltipPosition = t12;
    let t13;
    if ($[20] !== onHoverChange) {
      t13 = (dimension_0, event) => {
        onHoverChange?.(dimension_0.key);
        updateTooltipPosition(event.clientX, event.clientY);
      };
      $[20] = onHoverChange;
      $[21] = t13;
    } else {
      t13 = $[21];
    }
    const handleSegmentEnter = t13;
    let t14;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = event_0 => {
        updateTooltipPosition(event_0.clientX, event_0.clientY);
      };
      $[22] = t14;
    } else {
      t14 = $[22];
    }
    const handleSegmentMove = t14;
    let t15;
    if ($[23] !== onHoverChange) {
      t15 = () => {
        onHoverChange?.(null);
      };
      $[23] = onHoverChange;
      $[24] = t15;
    } else {
      t15 = $[24];
    }
    const handleTrackLeave = t15;
    if ($[25] !== className) {
      t8 = cn("relative", className);
      $[25] = className;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        height: powerBarLayoutBottomSpacerPx()
      };
      t10 = <div className={cn("absolute right-0 left-0", POWER_BAR_TRACK_CLASS)} style={{
        top: STANDARD_POWER_BAR_LAYOUT.trackTopPx,
        height: STANDARD_POWER_BAR_LAYOUT.trackHeightPx
      }} />;
      $[27] = t10;
      $[28] = t9;
    } else {
      t10 = $[27];
      t9 = $[28];
    }
    if ($[29] !== dimensions || $[30] !== fillPercent || $[31] !== handleSegmentEnter || $[32] !== handleTrackLeave || $[33] !== hoveredKey) {
      t11 = fillPercent > 0 ? <div ref={trackRef} className="absolute left-0 flex" style={{
        top: STANDARD_POWER_BAR_LAYOUT.trackTopPx,
        height: STANDARD_POWER_BAR_LAYOUT.trackHeightPx,
        width: `${fillPercent}%`
      }} onMouseLeave={handleTrackLeave}>{dimensions.map(dimension_1 => {
          const isHovered = hoveredKey === dimension_1.key;
          const isDimmed = hoveredKey != null && !isHovered;
          return <div key={dimension_1.key} className={cn("h-full min-w-0 cursor-pointer transition-opacity", isDimmed && "opacity-35", isHovered && "opacity-100")} style={{
            flex: `${dimension_1.points} 1 0`,
            ...pieChartAccentStyle(dimension_1.color)
          }} onMouseEnter={event_1 => handleSegmentEnter(dimension_1, event_1)} onMouseMove={handleSegmentMove} />;
        })}</div> : null;
      $[29] = dimensions;
      $[30] = fillPercent;
      $[31] = handleSegmentEnter;
      $[32] = handleTrackLeave;
      $[33] = hoveredKey;
      $[34] = t11;
    } else {
      t11 = $[34];
    }
    T0 = FloatingTooltip;
    t4 = hoveredDimension != null;
    t5 = tooltipPos.x;
    t6 = tooltipPos.y;
    t7 = hoveredDimension ? <ChartTooltipPanel title={hoveredDimension.label} rows={[{
      label: "Score",
      value: formatScore(hoveredDimension.score, {
        decimals: 1
      })
    }, {
      label: "Weight",
      value: `${hoveredDimension.weight}%`
    }, {
      label: "Points",
      value: hoveredDimension.points.toFixed(2)
    }]} /> : null;
    $[3] = className;
    $[4] = dimensions;
    $[5] = fillPercent;
    $[6] = hoveredKey;
    $[7] = onHoverChange;
    $[8] = tooltipPos.x;
    $[9] = tooltipPos.y;
    $[10] = T0;
    $[11] = t10;
    $[12] = t11;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    T0 = $[10];
    t10 = $[11];
    t11 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  let t12;
  if ($[35] !== T0 || $[36] !== t4 || $[37] !== t5 || $[38] !== t6 || $[39] !== t7) {
    t12 = <T0 open={t4} x={t5} y={t6}>{t7}</T0>;
    $[35] = T0;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t7;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t10 || $[42] !== t11 || $[43] !== t12 || $[44] !== t8 || $[45] !== t9) {
    t13 = <div className={t8} style={t9}>{t10}{t11}{t12}</div>;
    $[41] = t10;
    $[42] = t11;
    $[43] = t12;
    $[44] = t8;
    $[45] = t9;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  return t13;
}